import React, { useMemo, useState } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { Alert, Flex, Form, Spin, Tooltip, Typography } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import Counter from "ui/Counter"
import useTicketedAvailability from "hooks/useTicketedAvailability"
import { formatDurationFields } from "utils/experience"
import { getMultiDaySpan, getSlotStartLabel } from "utils/experienceAvailability"
import { getSlotStatus, getTicketedChoice, trimTicketCounts } from "utils/ticketedBooking"
// The guest widget books ticketed experiences already; New Booking keeps its selection shape and rules.
import { getBookableTicketTypes } from "utils/experience"
import { getTicketRateComparison } from "pagesV2/BookingWidget/WidgetExperience/pricing"
import DateField from "./DateField"
import { GuestPrice } from "./GuestCountSection"
import TimeDurationSelector from "./TimeDurationSelector"
import styles from "./BookingForm.module.css"

const { Text } = Typography

const DATE_FORMAT = "YYYY-MM-DD"
const DURATION_KEY = "ticketed"
const EMPTY_TICKET_COUNTS = {}

/**
 * New Booking's fields for a ticketed experience (redesign, Figma 5795:175252): Total Guests, the
 * date, the experience's duration, a start time with its places left, then the guests shared out
 * between the ticket types. The same choices and limits as the guest widget's ticketed panel
 * (useTicketedAvailability, utils/ticketedBooking), kept in bookingSelections in the widget's shape
 * (date, scheduledSlot, timeSlot, totalGuests, ticketCounts), so the summaries describe and price it
 * the same way. `onChange` stores a change; the form prices it.
 */
const TicketedFields = ({ form, experience, experienceId, bookingSelections, onChange, dateError, setDateError, editingBookingUuid }) => {
  const { t } = useTranslation()
  const { date = null, scheduledSlot = null } = bookingSelections
  const ticketCounts = bookingSelections.ticketCounts || EMPTY_TICKET_COUNTS
  const [calendarMonth, setCalendarMonth] = useState(() => date || dayjs().format(DATE_FORMAT))
  const ticketTypes = useMemo(() => getBookableTicketTypes(experience), [experience])
  const minGuests = Math.max(1, Number(experience?.min_tickets) || 0)
  // Per the designer's note, Total Guests starts at 1 (or the experience's minimum).
  const totalGuests = Number(bookingSelections.totalGuests) || minGuests
  const isMultiDay = experience?.duration_type === "multi_day"
  const spanDays = isMultiDay ? getMultiDaySpan(experience) : 1

  const { bookableDates, daySlots, isSlotsLoading } = useTicketedAvailability({
    experienceId,
    experience,
    date,
    month: calendarMonth,
    isEmbedded: false,
    excludeBookingUuid: editingBookingUuid
  })
  const { selectedSlot, ticketsChosen, remaining, maxGuests, typeLimits, availabilityWarning } = getTicketedChoice({
    t,
    experience,
    ticketTypes,
    ticketCounts,
    daySlots,
    scheduledSlot,
    date,
    totalGuests,
    isSlotsLoading
  })
  const slotStatus = useMemo(() => getSlotStatus(experience, daySlots), [experience, daySlots])
  const { rates, baseRates } = getTicketRateComparison({ experience, date })

  const durationLabel = isMultiDay ? t("booking_widget.experience.duration_days", { count: spanDays }) : formatDurationFields(experience, t)
  const durationOptions = useMemo(() => [{ key: DURATION_KEY, value: durationLabel }], [durationLabel])
  const slotOptions = useMemo(
    () => daySlots.map((slot) => ({ key: { slot: slot.label, available: !slot.isSoldOut }, value: slot.label })),
    [daySlots]
  )

  const revalidateTickets = () => setTimeout(() => form.validateFields(["guestTotal"]).catch(() => {}), 0)

  const handleGuestsChange = (next) => {
    const nextTotal = Math.max(minGuests, next)
    onChange({ totalGuests: nextTotal, ticketCounts: trimTicketCounts(ticketTypes, ticketCounts, nextTotal) })
    revalidateTickets()
  }

  const handleDateChange = (nextDate) => {
    onChange({ date: nextDate ? dayjs(nextDate).format(DATE_FORMAT) : null, timeSlot: null, scheduledSlot: null })
    form.setFieldValue("time", null)
    setDateError?.(false)
  }

  // TimeDurationSelector is memoized, but the selection's onChange and the notes change with every
  // selection anyway, so a useCallback here would hold nothing.
  const handleSlotChange = (option) => {
    const slot = daySlots.find((item) => item.label === option?.value)
    if (!slot || slot.isSoldOut) return
    onChange({ timeSlot: slot.label, scheduledSlot: { id: slot.id, startsAt: slot.startsAt } })
  }

  const handleTicketsChange = (ticketTypeId, next) => {
    onChange({ ticketCounts: { ...ticketCounts, [ticketTypeId]: Math.max(0, next) } })
    revalidateTickets()
  }

  // The guests and tickets, checked by Continue and Confirm with the rest of step 1.
  const validateTickets = () => {
    if (totalGuests < minGuests) return Promise.reject(new Error(t("booking_widget.experience.errors.guests", { count: minGuests })))
    // "for 5 guests" before any ticket is chosen, "for 2 more guests" once some are.
    const ticketsKey = ticketsChosen > 0 ? "tickets" : "tickets_none"
    if (remaining > 0) return Promise.reject(new Error(t(`booking_widget.experience.errors.${ticketsKey}`, { count: remaining })))
    if (availabilityWarning) return Promise.reject(new Error(t("booking_widget.experience.availability_changed")))
    return Promise.resolve()
  }

  const getSlotNote = (option) => {
    const { isSoldOut = false, placesLeft = null } = slotStatus[option.value] || {}
    if (isSoldOut) return <span className={styles.timeChipNote}> · {t("booking_widget.experience.sold_out")}</span>
    if (placesLeft == null) return null
    return (
      <span className={`${styles.timeChipNote} ${styles.timeChipNoteWarning}`}>
        {" "}
        · {t("booking_widget.experience.places_left", { count: placesLeft })}
      </span>
    )
  }

  let startTimes
  if (!date) startTimes = <Text type="secondary">{t("booking_widget.experience.choose_date_first")}</Text>
  else if (isSlotsLoading) startTimes = <Spin size="small" />
  else if (slotOptions.length === 0) startTimes = <Text type="secondary">{t("booking_widget.experience.no_start_times")}</Text>
  else
    startTimes = (
      <TimeDurationSelector
        options={slotOptions}
        selectedValue={selectedSlot?.label || null}
        onChange={handleSlotChange}
        formatLabel={getSlotStartLabel}
        getOptionNote={getSlotNote}
      />
    )

  return (
    <>
      <Flex align="center" justify="space-between" gap="var(--spacing-s)" className={styles.totalGuestsBox}>
        <Flex vertical>
          <span className={styles.totalGuestsTitle}>{t("booking.total_guests.title")}</span>
          {maxGuests > 0 && <span className={styles.totalGuestsLimit}>{t("booking.total_guests.limit", { max: maxGuests })}</span>}
        </Flex>
        <Counter
          value={totalGuests}
          decreaseLabel={t("booking.total_guests.fewer")}
          increaseLabel={t("booking.total_guests.more")}
          isDecreaseDisabled={totalGuests <= minGuests}
          isIncreaseDisabled={maxGuests > 0 && totalGuests >= maxGuests}
          onChange={handleGuestsChange}
        />
      </Flex>

      <DateField
        trip={experience}
        value={date ? dayjs(date) : null}
        onChange={handleDateChange}
        label={t("booking.labels.booked_date")}
        error={dateError}
        errorMessage={t("booking.error_messages.date_required")}
        availableDates={bookableDates}
        spanDays={spanDays}
        onMonthChange={(month) => setCalendarMonth(dayjs(month).format(DATE_FORMAT))}
        // The guide may book a departure the guest widget no longer offers (the backend allows past
        // and cut-off start times on the guide portal); the days on offer come from the API.
        allowPastDates
      />

      {durationLabel && (
        <Form.Item label={t("booking.labels.duration")} required>
          <TimeDurationSelector options={durationOptions} selectedValue={DURATION_KEY} onChange={() => {}} />
        </Form.Item>
      )}

      <Form.Item
        label={t("booking.labels.select_start_time")}
        name="time"
        rules={[{ required: true, message: t("booking.error_messages.time_required") }]}
      >
        {startTimes}
      </Form.Item>

      <div className={styles.guestSection}>
        <Flex align="center" gap="var(--spacing-s)" className={styles.guestTitle}>
          <span>
            {t("booking.ticketed.tickets_title")}
            <span className={styles.requiredMark}> *</span>
          </span>
          <span className={styles.guestLimits}>
            {remaining > 0
              ? t("booking_widget.experience.tickets_remaining", { count: remaining })
              : t("booking_widget.experience.max_places_reached", { count: totalGuests })}
          </span>
        </Flex>
        {availabilityWarning && (
          <Alert
            type="warning"
            showIcon
            className="mb-s"
            message={t("booking_widget.experience.availability_changed_detail", { detail: availabilityWarning })}
          />
        )}
        <div className={styles.guestBox}>
          {ticketTypes.map((ticketType) => {
            const count = Number(ticketCounts[ticketType.id] || 0)
            const hasLiveLimit = typeLimits?.[ticketType.id] != null
            const typeLimit = hasLiveLimit ? typeLimits[ticketType.id] : Number(ticketType.max_capacity) || 0
            const isAtTypeLimit = (hasLiveLimit || typeLimit > 0) && count >= typeLimit
            return (
              <Flex key={ticketType.id} align="flex-start" justify="space-between" gap="var(--spacing-xs)" className={styles.guestRow}>
                <Flex vertical>
                  <Flex align="center" gap="var(--spacing-xs)">
                    <span className={styles.guestLabel}>{ticketType.title}</span>
                    {ticketType.description && (
                      <Tooltip title={ticketType.description}>
                        <InfoCircleOutlined
                          className={styles.guestInfoIcon}
                          aria-label={t("booking_widget.experience.ticket_types.info", { title: ticketType.title })}
                        />
                      </Tooltip>
                    )}
                  </Flex>
                  <GuestPrice rate={Number(rates[ticketType.id]) || 0} baseRate={Number(baseRates[ticketType.id]) || 0} />
                </Flex>
                <Counter
                  value={count}
                  decreaseLabel={t("booking_widget.experience.decrease", { type: ticketType.title })}
                  increaseLabel={t("booking_widget.experience.increase", { type: ticketType.title })}
                  isDecreaseDisabled={count === 0}
                  isIncreaseDisabled={remaining === 0 || isAtTypeLimit}
                  onChange={(next) => handleTicketsChange(ticketType.id, next)}
                />
              </Flex>
            )
          })}
        </div>
        <Form.Item name="guestTotal" rules={[{ validator: validateTickets }]} className={styles.guestTotalItem} />
      </div>
    </>
  )
}

TicketedFields.propTypes = {
  form: PropTypes.object.isRequired,
  experience: PropTypes.object.isRequired,
  experienceId: PropTypes.string.isRequired,
  bookingSelections: PropTypes.shape({
    date: PropTypes.string,
    scheduledSlot: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), startsAt: PropTypes.string }),
    totalGuests: PropTypes.number,
    ticketCounts: PropTypes.object
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  dateError: PropTypes.bool,
  setDateError: PropTypes.func,
  // Edit Booking: the booking's uuid, so its own places count as free on its departures.
  editingBookingUuid: PropTypes.string
}

TicketedFields.defaultProps = {
  dateError: false,
  setDateError: null,
  editingBookingUuid: null
}

export default TicketedFields
